import React, { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { Package, ShoppingCart, Plus, Edit, Trash2, Truck, Hand, Tag, Sparkles, Minus, X, Upload, ImageIcon } from 'lucide-react';

const fmt = (n) => `£${(Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Resize an uploaded image to max 800px on the longest edge and return a
 * compressed JPEG data URL (quality 0.82). Keeps payload typically <100 KB
 * which fits comfortably in the existing `image_url` field.
 */
const fileToCompressedDataUrl = (file) => new Promise((resolve, reject) => {
  if (!file.type.startsWith('image/')) return reject(new Error('Not an image'));
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const MAX = 800;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const ratio = Math.min(MAX / width, MAX / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

/** Build a sensible default title from the listing's structured fields. */
const suggestTitle = (form) => {
  const parts = [
    (form.stone_colour || '').trim(),
    (form.shape || '').trim(),
    (form.finish || '').trim().toLowerCase() === 'polished' ? '' : (form.finish || '').trim(),
    'Memorial',
  ].filter(Boolean);
  return parts.join(' ').replace(/\s+/g, ' ').trim();
};

// Common UK memorial values for type-ahead suggestions on the listing form.
const COLOUR_SUGGESTIONS = ['Light Grey', 'Mid Grey', 'Dark Grey', 'Black', 'Bahama Blue', 'Blue Pearl', 'Red Multi', 'Brown', 'Green', 'White Marble', 'Honed Black', 'Karin Grey'];
const SHAPE_SUGGESTIONS = ['Ogee', 'Round Top', 'Gothic', 'Heart', 'Cross', 'Oval', 'Book', 'Scroll', 'Pillow', 'Rustic Boulder', 'Cremation Tablet', 'Kerb Set', 'Vase'];
const FINISH_SUGGESTIONS = ['Polished', 'Honed', 'Pitched', 'Sandblasted', 'Sawn', 'Part-polished'];

/** Thumbnail picker: shows preview + Upload + Clear. Stores result on `value`. */
function ImagePicker({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting same file
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image must be under 8 MB.');
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      onChange(dataUrl);
    } catch {
      toast.error('Could not read image. Try a different file.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-start gap-3">
      <div className="w-24 h-24 rounded border border-slate-200 bg-slate-50 grid place-items-center overflow-hidden flex-shrink-0">
        {value ? (
          <img src={value} alt="Listing" className="w-full h-full object-cover" />
        ) : (
          <ImageIcon className="w-8 h-8 text-slate-300" />
        )}
      </div>
      <div className="flex-1 space-y-2">
        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer text-sm">
          <Upload className="w-4 h-4" />
          <span>{busy ? 'Processing…' : value ? 'Replace image' : 'Upload image'}</span>
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={testid} />
        </label>
        {value && (
          <Button type="button" size="sm" variant="ghost" className="text-red-600 h-8 px-2" onClick={() => onChange('')}>
            <X className="w-3.5 h-3.5 mr-1" /> Remove
          </Button>
        )}
        <p className="text-[11px] text-slate-500">JPG/PNG up to 8 MB. Auto-resized to 800px for fast loading.</p>
      </div>
    </div>
  );
}

/**
 * Stock Memorials — pallet-basket model.
 *
 * Masons "Add to Pallet" multiple stones. A floating Cart Summary card
 * tracks the basket and applies ONE pallet delivery fee for the whole
 * order. Submitting builds a single group_order with N components.
 *
 * Set In Stone admins also see Edit / Delete icons on each card.
 */
export default function StockMemorialsSection({ onOrderPlaced }) {
  const { token, user, API } = useContext(AuthContext);
  const canManage = user?.role === 'super_admin' || user?.company_id === 'set-in-stone';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Cart: { [stock_id]: { item, qty } }
  const [cart, setCart] = useState({});
  const [deliveryMethod, setDeliveryMethod] = useState('pallet'); // pallet | collect
  const [cartNotes, setCartNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Edit/Delete state for sellers
  const [editDialog, setEditDialog] = useState({ open: false, item: null });
  const [manageDialog, setManageDialog] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/stock-memorials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(r.data || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  useEffect(() => { load(); }, [load]);

  const addToCart = (item, addQty = 1) => {
    const want = Math.max(1, parseInt(addQty, 10) || 1);
    setCart(prev => {
      const cur = prev[item.id]?.qty || 0;
      const max = item.qty_available;
      if (cur + want > max) {
        toast.error(`Only ${max} available.`);
        return prev;
      }
      return { ...prev, [item.id]: { item, qty: cur + want } };
    });
  };

  const setCartQty = (stockId, qty) => {
    setCart(prev => {
      const entry = prev[stockId];
      if (!entry) return prev;
      const safeQty = Math.max(0, Math.min(parseInt(qty) || 0, entry.item.qty_available));
      if (safeQty === 0) {
        const next = { ...prev };
        delete next[stockId];
        return next;
      }
      return { ...prev, [stockId]: { ...entry, qty: safeQty } };
    });
  };

  const removeFromCart = (stockId) => {
    setCart(prev => {
      const next = { ...prev };
      delete next[stockId];
      return next;
    });
  };

  const cartEntries = Object.values(cart);
  const stonesSubtotal = cartEntries.reduce((s, e) => s + (Number(e.item.price_each) || 0) * e.qty, 0);
  // Pallet charge: use the highest pallet_delivery_cost in the cart, since
  // any single pallet must cover the heaviest line. Fall back to 0.
  const palletCost = deliveryMethod === 'pallet'
    ? cartEntries.reduce((max, e) => Math.max(max, Number(e.item.pallet_delivery_cost) || 0), 0)
    : 0;
  const subtotal = stonesSubtotal + palletCost;
  const vatRate = cartEntries[0]?.item?.vat_rate ?? 20;
  const vat = subtotal * (Number(vatRate) / 100);
  const total = subtotal + vat;

  const submitCart = async () => {
    if (cartEntries.length === 0) { toast.error('Cart is empty.'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/stock-memorials/order-cart`, {
        items: cartEntries.map(e => ({ stock_id: e.item.id, qty: e.qty })),
        delivery_method: deliveryMethod,
        pallet_charge: deliveryMethod === 'pallet' ? palletCost : null,
        notes: cartNotes
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Order placed — ${r.data.group_order.reference_number}`);
      setCart({});
      setCartNotes('');
      load();
      if (onOrderPlaced) onOrderPlaced();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500 py-8 text-center">Loading stock…</div>;
  }

  return (
    <section className="space-y-3" data-testid="stock-memorials-section">
      {/* Shared datalists for type-ahead suggestions inside the listing dialogs */}
      <datalist id="dl-stone-colours">{COLOUR_SUGGESTIONS.map(v => <option key={v} value={v} />)}</datalist>
      <datalist id="dl-stone-shapes">{SHAPE_SUGGESTIONS.map(v => <option key={v} value={v} />)}</datalist>
      <datalist id="dl-stone-finishes">{FINISH_SUGGESTIONS.map(v => <option key={v} value={v} />)}</datalist>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center text-white shadow-sm">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Stones Available Now</h2>
            <p className="text-sm text-slate-500">In-stock memorials for immediate purchase. Build a pallet by adding multiple items — one delivery charge covers the lot.</p>
          </div>
        </div>
        {canManage && (
          <Button variant="outline" size="sm" onClick={() => setManageDialog(true)} data-testid="manage-stock-btn">
            <Plus className="w-4 h-4 mr-2" /> List My Stock
          </Button>
        )}
      </div>

      <div className={cartEntries.length > 0 ? 'grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4' : ''}>
        <div>
          {items.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center">
                <Package className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">
                  {canManage
                    ? 'No stock listings yet. Click "List My Stock" to add yard stock for trade sale.'
                    : 'No stock available at the moment. Check back soon.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map(item => (
                <StockCard
                  key={item.id}
                  item={item}
                  ownListing={item.seller_company_id === user?.company_id}
                  canManage={canManage}
                  inCart={cart[item.id]?.qty || 0}
                  onAdd={addToCart}
                  onSetQty={setCartQty}
                  onEdit={() => setEditDialog({ open: true, item })}
                  onDelete={async () => {
                    if (!window.confirm(`Delete "${item.title}"? This can't be undone.`)) return;
                    try {
                      await axios.delete(`${API}/stock-memorials/${item.id}`, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success('Listing deleted.');
                      load();
                    } catch (e) {
                      toast.error('Delete failed');
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {cartEntries.length > 0 && (
          <CartSummary
            entries={cartEntries}
            deliveryMethod={deliveryMethod}
            setDeliveryMethod={setDeliveryMethod}
            palletCost={palletCost}
            stonesSubtotal={stonesSubtotal}
            vat={vat}
            total={total}
            vatRate={vatRate}
            cartNotes={cartNotes}
            setCartNotes={setCartNotes}
            onSetQty={setCartQty}
            onRemove={removeFromCart}
            onSubmit={submitCart}
            submitting={submitting}
          />
        )}
      </div>

      {canManage && (
        <ManageStockDialog open={manageDialog} onOpenChange={setManageDialog} onChanged={load} />
      )}
      <EditListingDialog dialog={editDialog} setDialog={setEditDialog} onSaved={load} />
    </section>
  );
}

function StockCard({ item, ownListing, canManage, inCart, onAdd, onSetQty, onEdit, onDelete }) {
  const lowStock = item.qty_available > 0 && item.qty_available <= 3;
  const outOfStock = item.qty_available <= 0;
  const [addQty, setAddQty] = useState(1);
  return (
    <Card className={`overflow-hidden transition-all hover:shadow-md ${outOfStock ? 'opacity-60' : ''}`} data-testid="stock-card">
      <div className="aspect-[4/3] bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden">
        {item.image_url ? (
          <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-400">
            <Package className="w-12 h-12" />
          </div>
        )}
        {outOfStock && <Badge variant="destructive" className="absolute top-2 left-2">Out of Stock</Badge>}
        {!outOfStock && lowStock && <Badge className="absolute top-2 left-2 bg-amber-500 hover:bg-amber-600">Only {item.qty_available} left</Badge>}
        {!outOfStock && !lowStock && <Badge className="absolute top-2 left-2 bg-emerald-500 hover:bg-emerald-600">In Stock ({item.qty_available})</Badge>}
        {ownListing && <Badge className="absolute top-2 right-2 bg-indigo-600 hover:bg-indigo-600">Your listing</Badge>}

        {/* Seller-side controls overlay — visible to anyone allowed to manage stock
            (Set In Stone admins + super-admins). The backend enforces the same rule. */}
        {canManage && (
          <div className="absolute bottom-2 right-2 flex gap-1">
            <Button size="sm" variant="secondary" className="h-7 w-7 p-0 bg-white/90 hover:bg-white shadow" onClick={onEdit} title="Edit listing" data-testid={`edit-stock-${item.id}`}>
              <Edit className="w-3.5 h-3.5 text-slate-700" />
            </Button>
            <Button size="sm" variant="secondary" className="h-7 w-7 p-0 bg-white/90 hover:bg-red-50 shadow" onClick={onDelete} title="Delete listing" data-testid={`delete-stock-${item.id}`}>
              <Trash2 className="w-3.5 h-3.5 text-red-600" />
            </Button>
          </div>
        )}
      </div>
      <CardContent className="p-4 space-y-3">
        <div>
          <h3 className="font-semibold text-slate-900 leading-tight">{item.title}</h3>
          {item.seller_company_name && (
            <p className="text-[11px] text-slate-500 mt-0.5">Sold by <span className="font-medium text-slate-700">{item.seller_company_name}</span></p>
          )}
          {item.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{item.description}</p>}
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="bg-slate-50 rounded px-2 py-1">
            <p className="text-slate-400">Colour</p>
            <p className="font-medium text-slate-700 truncate">{item.stone_colour}</p>
          </div>
          {item.shape && (
            <div className="bg-slate-50 rounded px-2 py-1">
              <p className="text-slate-400">Shape</p>
              <p className="font-medium text-slate-700 truncate">{item.shape}</p>
            </div>
          )}
          {item.size && (
            <div className="bg-slate-50 rounded px-2 py-1">
              <p className="text-slate-400">Size</p>
              <p className="font-medium text-slate-700 truncate">{item.size}</p>
            </div>
          )}
          {item.finish && (
            <div className="bg-slate-50 rounded px-2 py-1">
              <p className="text-slate-400">Finish</p>
              <p className="font-medium text-slate-700 truncate">{item.finish}</p>
            </div>
          )}
        </div>
        <div className="flex items-end justify-between pt-2 border-t border-slate-100">
          <div>
            <p className="text-xs text-slate-400">Price each</p>
            <p className="text-xl font-bold text-blue-700">{fmt(item.price_each)}<span className="text-xs text-slate-400 font-normal"> + VAT</span></p>
          </div>
          {inCart > 0 ? (
            <div className="flex items-center gap-1 border rounded-md" data-testid={`stock-cart-qty-${item.id}`}>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onSetQty(item.id, inCart - 1)}>
                <Minus className="w-3 h-3" />
              </Button>
              <span className="w-8 text-center text-sm font-medium">{inCart}</span>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onSetQty(item.id, inCart + 1)} disabled={inCart >= item.qty_available}>
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <Input
                type="number"
                min={1}
                max={item.qty_available || 1}
                value={addQty}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (Number.isNaN(n)) { setAddQty(1); return; }
                  const max = item.qty_available || 1;
                  setAddQty(Math.min(Math.max(n, 1), max));
                }}
                disabled={outOfStock || ownListing}
                className="h-8 w-14 text-center text-sm"
                data-testid={`stock-add-qty-${item.id}`}
                title="Quantity to add"
              />
              <Button
                size="sm"
                onClick={() => onAdd(item, addQty)}
                disabled={outOfStock || ownListing}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid={`stock-add-${item.id}`}
                title={ownListing ? "You can't order your own stock" : ''}
              >
                <ShoppingCart className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between text-[10px] text-slate-400">
          <span className="flex items-center gap-1"><Truck className="w-3 h-3" /> Pallet {fmt(item.pallet_delivery_cost)}</span>
          {item.collection_free && <span className="flex items-center gap-1 text-emerald-600 font-medium"><Hand className="w-3 h-3" /> Free collect</span>}
        </div>
      </CardContent>
    </Card>
  );
}

function CartSummary({ entries, deliveryMethod, setDeliveryMethod, palletCost, stonesSubtotal, vat, total, vatRate, cartNotes, setCartNotes, onSetQty, onRemove, onSubmit, submitting }) {
  return (
    <Card className="sticky top-4 self-start" data-testid="cart-summary">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><ShoppingCart className="w-4 h-4 text-blue-600" /> Your Pallet</h3>
          <Badge variant="outline" className="text-xs">{entries.length} line{entries.length === 1 ? '' : 's'}</Badge>
        </div>

        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {entries.map(e => (
            <div key={e.item.id} className="flex items-start gap-2 text-sm">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800 truncate text-xs">{e.item.title}</p>
                <p className="text-[11px] text-slate-500">{fmt(e.item.price_each)} × {e.qty} = <span className="text-slate-700">{fmt((Number(e.item.price_each)||0)*e.qty)}</span></p>
              </div>
              <div className="flex items-center gap-1 border rounded">
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onSetQty(e.item.id, e.qty - 1)}>
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="w-5 text-center text-xs">{e.qty}</span>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onSetQty(e.item.id, e.qty + 1)} disabled={e.qty >= e.item.qty_available}>
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-600" onClick={() => onRemove(e.item.id)}>
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>

        <div className="border-t pt-2 space-y-2">
          <Label className="text-xs">Delivery</Label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setDeliveryMethod('pallet')}
              className={`flex-1 text-xs px-2 py-1.5 rounded-md border transition ${deliveryMethod === 'pallet' ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-300 text-slate-600 hover:border-blue-400'}`}
            >
              <Truck className="w-3 h-3 inline mr-1" /> Pallet {fmt(palletCost)}
            </button>
            <button
              type="button"
              onClick={() => setDeliveryMethod('collect')}
              className={`flex-1 text-xs px-2 py-1.5 rounded-md border transition ${deliveryMethod === 'collect' ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-300 text-slate-600 hover:border-emerald-400'}`}
            >
              <Hand className="w-3 h-3 inline mr-1" /> Collect FREE
            </button>
          </div>
          <Textarea
            rows={2}
            value={cartNotes}
            onChange={(e) => setCartNotes(e.target.value)}
            placeholder="Notes for this order…"
            className="text-xs"
          />
        </div>

        <div className="border-t pt-2 text-xs space-y-1">
          <div className="flex justify-between"><span className="text-slate-500">Stones</span><span>{fmt(stonesSubtotal)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Delivery</span><span>{palletCost ? fmt(palletCost) : <span className="text-emerald-600 font-medium">FREE</span>}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">VAT ({vatRate}%)</span><span>{fmt(vat)}</span></div>
          <div className="flex justify-between text-base font-bold pt-1 border-t"><span>Total</span><span className="text-blue-700">{fmt(total)}</span></div>
        </div>

        <Button onClick={onSubmit} disabled={submitting || entries.length === 0} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="cart-submit">
          {submitting ? 'Placing order…' : 'Place Order'}
        </Button>
      </CardContent>
    </Card>
  );
}

function EditListingDialog({ dialog, setDialog, onSaved }) {
  const { token, API } = useContext(AuthContext);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (dialog.open && dialog.item) {
      setForm({ ...dialog.item });
    }
  }, [dialog.open, dialog.item]);

  const save = async () => {
    if (!dialog.item) return;
    if (!form.title || !form.stone_colour) {
      toast.error('Title and Stone Colour are required.');
      return;
    }
    setSaving(true);
    try {
      const patch = {
        title: form.title,
        description: form.description,
        stone_colour: form.stone_colour,
        shape: form.shape,
        finish: form.finish,
        size: form.size,
        qty_available: Number(form.qty_available) || 0,
        price_each: Number(form.price_each) || 0,
        vat_rate: Number(form.vat_rate) || 20,
        pallet_delivery_cost: Number(form.pallet_delivery_cost) || 0,
        collection_free: !!form.collection_free,
        image_url: form.image_url,
        active: !!form.active,
        notes: form.notes,
      };
      await axios.patch(`${API}/stock-memorials/${dialog.item.id}`, patch, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Listing updated.');
      setDialog({ open: false, item: null });
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, item: null })}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Edit className="w-5 h-5 text-blue-600" /> Edit Listing</DialogTitle>
          <DialogDescription>Update stock listing details. Changes are visible to all masons immediately.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Title *</Label>
              <button
                type="button"
                onClick={() => setForm({ ...form, title: suggestTitle(form) })}
                className="text-[11px] text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-40"
                disabled={!form.stone_colour && !form.shape}
                title="Build a title from Colour + Shape + Finish"
                data-testid="edit-stock-suggest-title"
              >
                ✨ Suggest from details
              </button>
            </div>
            <Input value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Light Grey Ogee Memorial" />
          </div>
          <div><Label>Stone Colour *</Label><Input list="dl-stone-colours" value={form.stone_colour || ''} onChange={(e) => setForm({ ...form, stone_colour: e.target.value })} /></div>
          <div><Label>Shape</Label><Input list="dl-stone-shapes" value={form.shape || ''} onChange={(e) => setForm({ ...form, shape: e.target.value })} /></div>
          <div><Label>Finish</Label><Input list="dl-stone-finishes" value={form.finish || ''} onChange={(e) => setForm({ ...form, finish: e.target.value })} /></div>
          <div><Label>Size</Label><Input value={form.size || ''} onChange={(e) => setForm({ ...form, size: e.target.value })} /></div>
          <div><Label>Qty Available</Label><Input type="number" min={0} value={form.qty_available ?? 0} onChange={(e) => setForm({ ...form, qty_available: e.target.value })} /></div>
          <div><Label>Price each £</Label><Input type="number" step="0.01" value={form.price_each ?? 0} onChange={(e) => setForm({ ...form, price_each: e.target.value })} /></div>
          <div><Label>VAT %</Label><Input type="number" step="0.1" value={form.vat_rate ?? 20} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} /></div>
          <div><Label>Pallet £</Label><Input type="number" step="0.01" value={form.pallet_delivery_cost ?? 70} onChange={(e) => setForm({ ...form, pallet_delivery_cost: e.target.value })} /></div>
          <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="sm:col-span-2">
            <Label>Listing image</Label>
            <ImagePicker value={form.image_url || ''} onChange={(v) => setForm({ ...form, image_url: v })} testid="edit-stock-image-upload" />
          </div>
          <div className="sm:col-span-2 flex items-center gap-4 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              <span>Active</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.collection_free} onChange={(e) => setForm({ ...form, collection_free: e.target.checked })} />
              <span>Free collection</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDialog({ open: false, item: null })}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="edit-stock-save">
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageStockDialog({ open, onOpenChange, onChanged }) {
  const { token, API } = useContext(AuthContext);
  const empty = {
    title: '', description: '', stone_colour: '', shape: '', finish: 'Polished',
    size: '', qty_available: 0, price_each: 0, vat_rate: 20, pallet_delivery_cost: 70,
    collection_free: true, image_url: '', active: true, notes: ''
  };
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  // Items list scoped to my company so the admin only sees & edits their own.
  const [myItems, setMyItems] = useState([]);

  const fetchMine = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/stock-memorials`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { mine_only: true, include_inactive: true }
      });
      setMyItems(r.data || []);
    } catch (e) {
      setMyItems([]);
    }
  }, [API, token]);

  useEffect(() => {
    if (open) fetchMine();
  }, [open, fetchMine]);

  const refreshAll = () => { fetchMine(); onChanged(); };

  const startEdit = (item) => {
    setEditing(item.id);
    setForm({ ...empty, ...item });
  };
  const startNew = () => {
    setEditing(null);
    setForm(empty);
  };

  const save = async () => {
    if (!form.title || !form.stone_colour) {
      toast.error('Title and Stone Colour are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, qty_available: Number(form.qty_available) || 0, price_each: Number(form.price_each) || 0, vat_rate: Number(form.vat_rate) || 0, pallet_delivery_cost: Number(form.pallet_delivery_cost) || 0 };
      if (editing) {
        await axios.patch(`${API}/stock-memorials/${editing}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Stock item updated.');
      } else {
        await axios.post(`${API}/stock-memorials`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Stock item added.');
      }
      startNew();
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this stock item? Existing orders are unaffected.')) return;
    try {
      await axios.delete(`${API}/stock-memorials/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Stock item deleted.');
      refreshAll();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Tag className="w-5 h-5 text-amber-500" /> My Stock Listings</DialogTitle>
          <DialogDescription>Add yard stock you want to offer for sale to other trade masons. Buyers see all active listings across all sellers.</DialogDescription>
        </DialogHeader>

        {/* My listings */}
        <div className="space-y-2 mb-4">
          {myItems.length === 0 ? (
            <p className="text-sm text-slate-500">You haven&apos;t listed any stock yet.</p>
          ) : myItems.map(it => (
            <div key={it.id} className="flex items-center gap-3 p-2 rounded border border-slate-200">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-slate-900 truncate">{it.title}</p>
                <p className="text-xs text-slate-500">{it.stone_colour} · qty {it.qty_available} · {fmt(it.price_each)} ea {it.active ? '' : '· INACTIVE'}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => startEdit(it)}><Edit className="w-3 h-3" /></Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(it.id)}><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
        </div>

        {/* Editor form */}
        <div className="border-t pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">{editing ? 'Edit item' : 'Add new item'}</h3>
            {editing && <Button size="sm" variant="ghost" onClick={startNew}>Cancel edit</Button>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between">
                <Label>Title *</Label>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, title: suggestTitle(form) })}
                  className="text-[11px] text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-40"
                  disabled={!form.stone_colour && !form.shape}
                  title="Build a title from Colour + Shape + Finish"
                  data-testid="manage-stock-suggest-title"
                >
                  ✨ Suggest from details
                </button>
              </div>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Light Grey Ogee Memorial" />
            </div>
            <div>
              <Label>Stone Colour *</Label>
              <Input list="dl-stone-colours" value={form.stone_colour} onChange={(e) => setForm({ ...form, stone_colour: e.target.value })} placeholder="Light Grey" />
            </div>
            <div>
              <Label>Shape</Label>
              <Input list="dl-stone-shapes" value={form.shape} onChange={(e) => setForm({ ...form, shape: e.target.value })} placeholder="Ogee" />
            </div>
            <div>
              <Label>Finish</Label>
              <Input list="dl-stone-finishes" value={form.finish} onChange={(e) => setForm({ ...form, finish: e.target.value })} placeholder="Polished" />
            </div>
            <div>
              <Label>Size</Label>
              <Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="Standard / 27 x 21 x 3&quot;" />
            </div>
            <div>
              <Label>Qty Available</Label>
              <Input type="number" min={0} value={form.qty_available} onChange={(e) => setForm({ ...form, qty_available: e.target.value })} />
            </div>
            <div>
              <Label>Price Each (ex VAT)</Label>
              <Input type="number" step="0.01" value={form.price_each} onChange={(e) => setForm({ ...form, price_each: e.target.value })} />
            </div>
            <div>
              <Label>VAT %</Label>
              <Input type="number" step="0.1" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} />
            </div>
            <div>
              <Label>Pallet Delivery £</Label>
              <Input type="number" step="0.01" value={form.pallet_delivery_cost} onChange={(e) => setForm({ ...form, pallet_delivery_cost: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Listing image</Label>
              <ImagePicker value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="manage-stock-image-upload" />
            </div>
            <div className="sm:col-span-2 flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                <span>Active (visible to masons)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.collection_free} onChange={(e) => setForm({ ...form, collection_free: e.target.checked })} />
                <span>Free collection available</span>
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? 'Saving…' : (editing ? 'Update Stock' : 'Add Stock')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
